import { useCallback, useMemo, useState } from 'react';
import { collectTagOptions, filterStudies, matchesSearch, tagKey } from '../services/imagingTags';

export function useStudyListControls(studies) {
    const [search, setSearch] = useState('');
    const [selectedTagKeys, setSelectedTagKeys] = useState(() => new Set());

    // Tag chips reflect only the studies that match the current search phrase,
    // so typing narrows the available tags (and their counts) along with the list.
    const searchedStudies = useMemo(
        () => (studies || []).filter((s) => matchesSearch(s, search)),
        [studies, search]
    );

    const tagOptions = useMemo(() => collectTagOptions(searchedStudies), [searchedStudies]);

    const activeTagKeys = useMemo(() => {
        const available = new Set(tagOptions.map((opt) => tagKey(opt.value)));
        const active = new Set();
        selectedTagKeys.forEach((key) => {
            if (available.has(key)) active.add(key);
        });
        return active;
    }, [selectedTagKeys, tagOptions]);

    const toggleTag = useCallback((key) => {
        setSelectedTagKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const clearFilters = useCallback(() => {
        setSearch('');
        setSelectedTagKeys(new Set());
    }, []);

    const visibleStudies = useMemo(
        () => filterStudies(searchedStudies, { search: '', selectedTagKeys: activeTagKeys }),
        [searchedStudies, activeTagKeys]
    );

    const hasActiveFilters = search.trim().length > 0 || activeTagKeys.size > 0;

    return {
        search,
        setSearch,
        tagOptions,
        selectedTagKeys: activeTagKeys,
        toggleTag,
        clearFilters,
        visibleStudies,
        hasActiveFilters,
    };
}
